import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Alert from '@mui/material/Alert';

/**
 * PGSS-5746: a stock product's name is built from colour, type, size and polish, and staff kept creating one that
 * already existed (16 names were shared by 43 stock products on 15 Oct 2026; PGSS-5745). This warns, under the name,
 * when another stock product reads the same: case, extra spaces and the "×" shown for "x" are ignored by the API.
 * It is a warning, not a block: a genuine variant may share a name until SKUs tell them apart.
 */
export const SAME_NAME_DEBOUNCE_MS = 400;

export function sameNameMessage(matches) {
  if (!matches?.length) return null;
  return matches.length === 1
    ? 'A stock product with this name already exists. Open it instead?'
    : `${matches.length} stock products with this name already exist. Open one instead?`;
}

export default function SameNameStockWarning({ name, productId, enabled }) {
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    const trimmed = (name || '').trim();
    if (!enabled || !trimmed) {
      setMatches([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      axios
        .get('/api/products/name-matches', { params: { name: trimmed, excludeId: productId || undefined } })
        .then((response) => {
          if (!cancelled) setMatches(Array.isArray(response.data) ? response.data : []);
        })
        .catch(() => {
          // A warning only: if the check fails, saving still works as before.
          if (!cancelled) setMatches([]);
        });
    }, SAME_NAME_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [name, productId, enabled]);

  const message = sameNameMessage(matches);
  if (!message) return null;
  return (
    <Alert severity="warning" className="mb-16" data-testid="product-same-name-warning">
      {message}{' '}
      {matches.map((match, index) => (
        <span key={match.id}>
          {index > 0 ? ', ' : ''}
          <Link to={`/products/${match.id}`} target="_blank" rel="noopener noreferrer">
            {match.name}
          </Link>
        </span>
      ))}
    </Alert>
  );
}
